import { useMemo, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Check, Copy, AlertTriangle, TrendingUp, Users, DollarSign, Zap } from 'lucide-react';
import { useVendorRanking, useAreaBreakdown, useExecutiveSummary } from '@/hooks/useSubscriptions';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { useTranslation } from 'react-i18next';

const fmt = (n: number) =>
  `$${n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;

type Intensity = 'bajo' | 'medio' | 'alto' | 'power';
type TFunction = ReturnType<typeof useTranslation>['t'];

// Función de `t`: label/desc salen de i18n y `t` no existe a nivel de módulo.
const INTENSITY_CONFIG = (t: TFunction): Record<Intensity, { label: string; bg: string; text: string; desc: string }> => ({
  bajo:  { label: t('subscriptions.advisortab.intensity.bajo.label'), bg: '#F5F5F5', text: '#555555', desc: t('subscriptions.advisortab.intensity.bajo.desc') },
  medio: { label: t('subscriptions.advisortab.intensity.medio.label'), bg: '#EBF7FF', text: '#0C4A6E', desc: t('subscriptions.advisortab.intensity.medio.desc') },
  alto:  { label: t('subscriptions.advisortab.intensity.alto.label'), bg: '#F3EEFF', text: '#4C1D95', desc: t('subscriptions.advisortab.intensity.alto.desc') },
  power: { label: t('subscriptions.advisortab.intensity.power.label'), bg: '#FFFAE6', text: '#713F12', desc: t('subscriptions.advisortab.intensity.power.desc') },
});

function classifyIntensity(transactions: number, amount: number, users: number): Intensity {
  if (users > 5 && transactions > 15) return 'power';
  if (transactions > 10 || amount > 100) return 'alto';
  if (transactions > 2 || amount >= 20) return 'medio';
  return 'bajo';
}

function IntensityBadge({ level }: { level: Intensity }) {
  const { t } = useTranslation();
  const cfg = INTENSITY_CONFIG(t)[level];
  return (
    <span
      className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium border"
      style={{ backgroundColor: cfg.bg, color: cfg.text, borderColor: cfg.bg }}
    >
      {cfg.label}
    </span>
  );
}

export function AdvisorTab() {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const { data: summary = [] } = useExecutiveSummary();
  const { data: vendors = [] } = useVendorRanking();
  const { data: areaRows = [] } = useAreaBreakdown({});

  const isLoading = !summary.length && !vendors.length;

  // Latest month data
  const latestSummary = useMemo(() =>
    [...summary].sort((a, b) => b.Month.localeCompare(a.Month))[0],
    [summary]
  );

  // Detect Claude/Anthropic
  const claudeVendors = useMemo(() =>
    vendors.filter(v =>
      v.Normalized_Vendor.toLowerCase().includes('claude') ||
      v.Normalized_Vendor.toLowerCase().includes('anthropic')
    ),
    [vendors]
  );

  const hasClaudeData = claudeVendors.length > 0;
  const claudeTotalAmount = claudeVendors.reduce((s, v) => s + v.Total_Amount, 0);
  const claudeTotalUsers = Math.max(...claudeVendors.map(v => v.Users), 0);
  const claudeTotalTransactions = claudeVendors.reduce((s, v) => s + v.Transactions, 0);

  // All AI vendors for context
  const aiVendors = useMemo(() =>
    vendors.filter(v => v.Is_AI_Tool).sort((a, b) => b.Total_Amount - a.Total_Amount).slice(0, 10),
    [vendors]
  );

  // Area segmentation (using Claude rows if available, else all AI rows)
  const areaSegmentation = useMemo(() => {
    const relevantRows = hasClaudeData
      ? areaRows.filter(r =>
          r.Normalized_Vendor.toLowerCase().includes('claude') ||
          r.Normalized_Vendor.toLowerCase().includes('anthropic')
        )
      : areaRows.filter(r => r.Is_AI_Tool);

    const areaMap = new Map<string, { area: string; transactions: number; amount: number; users: number }>();
    for (const r of relevantRows) {
      const key = r.NetSuite_Departamentos || 'Sin área';
      const existing = areaMap.get(key) ?? { area: key, transactions: 0, amount: 0, users: 0 };
      existing.transactions += r.Transactions;
      existing.amount += r.Total_Amount;
      existing.users = Math.max(existing.users, r.Users);
      areaMap.set(key, existing);
    }
    return Array.from(areaMap.values())
      .map(a => ({ ...a, intensity: classifyIntensity(a.transactions, a.amount, a.users) }))
      .sort((a, b) => b.amount - a.amount);
  }, [areaRows, hasClaudeData]);

  const totalUsers = areaSegmentation.reduce((s, a) => s + a.users, 0) || claudeTotalUsers || latestSummary?.Users || 0;

  const countByIntensity = useMemo(() => {
    const counts = { bajo: 0, medio: 0, alto: 0, power: 0 };
    for (const a of areaSegmentation) counts[a.intensity] += a.users || 1;
    return counts;
  }, [areaSegmentation]);

  // Cost scenarios
  const scenarios = useMemo(() => {
    const n = Math.max(totalUsers, 1);
    const nPower = countByIntensity.power;
    const nAlto = countByIntensity.alto;
    const nRest = Math.max(n - nPower - nAlto, 0);

    return [
      {
        id: 'free',
        plan: t('subscriptions.advisortab.scenario.free.plan'),
        seats: n,
        costMonth: 0,
        ideal: t('subscriptions.advisortab.scenario.free.ideal'),
        viable: n < 3,
      },
      {
        id: 'pro',
        plan: t('subscriptions.advisortab.scenario.pro.plan'),
        seats: Math.max(nPower + nAlto, 1),
        costMonth: Math.max(nPower + nAlto, 1) * 20,
        ideal: t('subscriptions.advisortab.scenario.pro.ideal'),
        viable: nPower + nAlto <= 3,
      },
      {
        id: 'teamStandard',
        plan: t('subscriptions.advisortab.scenario.teamStandard.plan'),
        seats: n,
        costMonth: n * 25,
        ideal: t('subscriptions.advisortab.scenario.teamStandard.ideal'),
        viable: true,
      },
      {
        id: 'teamMixto',
        plan: t('subscriptions.advisortab.scenario.teamMixto.plan'),
        seats: n,
        costMonth: nRest * 25 + (nPower + nAlto) * 100,
        ideal: t('subscriptions.advisortab.scenario.teamMixto.ideal'),
        viable: nPower + nAlto > 0,
      },
      {
        id: 'enterprise',
        plan: 'Enterprise',
        seats: n,
        costMonth: -1,
        ideal: t('subscriptions.advisortab.scenario.enterprise.ideal'),
        viable: n > 100,
      },
    ];
  }, [totalUsers, countByIntensity, t]);

  const bestScenario = scenarios.find(s => s.viable && s.id === 'teamStandard') ?? scenarios[0];
  const altScenario = scenarios.find(s => s.viable && s.id !== bestScenario.id && s.costMonth >= 0 && s.costMonth < bestScenario.costMonth)
    ?? scenarios.find(s => s.id === 'pro');

  const suggestedMessage = `Hola! 👋

Hice un análisis del uso de IA en Winclap para recomendar la mejor suscripción de Claude.

📊 *Datos observados:*
${hasClaudeData ? `• Claude/Anthropic: ${fmt(claudeTotalAmount)} gastados, ${claudeTotalUsers} usuarios, ${claudeTotalTransactions} transacciones` : `• ${aiVendors.length} herramientas de IA activas, ${latestSummary?.Users ?? '?'} usuarios en total`}
• Total gasto IA: ${fmt(latestSummary?.AI_Tools_Amount ?? 0)}/mes

👥 *Usuarios identificados:* ~${totalUsers}
${areaSegmentation.slice(0, 4).map(a => `• ${a.area}: intensidad ${a.intensity}`).join('\n')}

💡 *Recomendación:* ${bestScenario.plan}
• ~${bestScenario.seats} seats × $${bestScenario.costMonth > 0 ? (bestScenario.costMonth / bestScenario.seats).toFixed(0) : 0}/mes = ${bestScenario.costMonth > 0 ? fmt(bestScenario.costMonth) : 'Custom'}/mes

💰 *Alternativa económica:* ${altScenario?.plan ?? 'Pro individual'} (~${altScenario?.costMonth && altScenario.costMonth > 0 ? fmt(altScenario.costMonth) : '$0'}/mes)

¿Querés que armemos el piloto con un grupo reducido primero?`;

  function copyMessage() {
    navigator.clipboard.writeText(suggestedMessage);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <WinclapLoader size={48} className="text-foreground/25" />
      </div>
    );
  }

  return (
    <div className="space-y-5 mt-4 max-w-4xl">

      {/* Header */}
      <div className="border-b border-brand-gray4 pb-4">
        <p className="text-[10px] font-medium tracking-widest text-brand-gray3 uppercase mb-1">{t('subscriptions.advisortab.ai_subscription_advisor')}</p>
        <h2 className="text-xl font-semibold text-brand-gray1 dark:text-white">
          {hasClaudeData ? t('subscriptions.advisortab.analisis_de_uso_de_claude_en') : t('subscriptions.advisortab.analisis_de_herramientas_ia_en_winclap')}
        </h2>
        <p className="text-sm text-muted-foreground mt-0.5">
          {t('subscriptions.advisortab.basado_en_datos_reales_de_transacciones')} {latestSummary?.Month ?? t('subscriptions.advisortab.periodo_disponible')}
        </p>
      </div>

      {/* 1. Resumen ejecutivo */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          {
            icon: DollarSign,
            label: hasClaudeData ? 'Gasto Claude' : 'Gasto IA total',
            value: fmt(hasClaudeData ? claudeTotalAmount : (latestSummary?.AI_Tools_Amount ?? 0)),
            color: '#1CC9B9',
          },
          {
            icon: Users,
            label: t('subscriptions.advisortab.usuarios_ia'),
            value: String(totalUsers || latestSummary?.Users || '—'),
            color: '#8b5cf6',
          },
          {
            icon: TrendingUp,
            label: t('subscriptions.advisortab.ai_share'),
            value: latestSummary ? `${((latestSummary.AI_Share ?? 0) * 100).toFixed(0)}%` : '—',
            color: '#f59e0b',
          },
          {
            icon: Zap,
            label: t('subscriptions.advisortab.vendors_ai'),
            value: String(latestSummary?.AI_Vendors ?? aiVendors.length),
            color: '#ef4444',
          },
        ].map(({ icon: Icon, label, value, color }) => (
          <Card key={label} className="p-4">
            <div className="flex items-center gap-2 mb-1">
              <Icon className="h-4 w-4" style={{ color }} />
              <p className="text-xs text-muted-foreground">{label}</p>
            </div>
            <p className="text-2xl font-semibold text-brand-gray1 dark:text-white">{value}</p>
          </Card>
        ))}
      </div>

      {/* Sin Claude — piloto */}
      {!hasClaudeData && (
        <Card className="p-4 border-amber-200 bg-amber-50 dark:bg-amber-900/10 dark:border-amber-800">
          <div className="flex gap-3">
            <AlertTriangle className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-amber-800 dark:text-amber-400">{t('subscriptions.advisortab.claude_no_aparece_en_los_datos')}</p>
              <p className="text-xs text-amber-700 dark:text-amber-500 mt-0.5">
                {t('subscriptions.advisortab.el_analisis_usa_el_total_de')}
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* 2. Datos observados — vendors AI */}
      <Card className="p-0 overflow-hidden">
        <div className="px-4 py-3 border-b">
          <p className="text-sm font-medium">{t('subscriptions.advisortab.herramientas_ia_observadas')}</p>
        </div>
        <table className="w-full text-xs">
          <thead className="bg-muted/40">
            <tr className="text-muted-foreground uppercase tracking-wide">
              <th className="text-left py-2 px-4 font-medium">Vendor</th>
              <th className="text-right py-2 px-3 font-medium">Gasto</th>
              <th className="text-right py-2 px-3 font-medium">Usuarios</th>
              <th className="text-right py-2 px-3 font-medium">Transacciones</th>
              <th className="text-center py-2 px-3 font-medium">Flag</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {aiVendors.map(v => {
              const isClaude = v.Normalized_Vendor.toLowerCase().includes('claude') ||
                v.Normalized_Vendor.toLowerCase().includes('anthropic');
              return (
                <tr key={v.Normalized_Vendor} className={isClaude ? 'bg-[#E5F9F7]/50' : ''}>
                  <td className="py-2 px-4 font-medium flex items-center gap-2">
                    {isClaude && <span className="text-brand-green text-[10px] font-bold">★</span>}
                    {v.Normalized_Vendor}
                  </td>
                  <td className="py-2 px-3 text-right font-semibold">{fmt(v.Total_Amount)}</td>
                  <td className="py-2 px-3 text-right text-muted-foreground">{v.Users}</td>
                  <td className="py-2 px-3 text-right text-muted-foreground">{v.Transactions}</td>
                  <td className="py-2 px-3 text-center">
                    {isClaude && <Badge variant="secondary" className="text-xs bg-[#E5F9F7] text-[#0D9488]">Claude</Badge>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      {/* 3. Segmentación por área */}
      {areaSegmentation.length > 0 && (
        <Card className="p-0 overflow-hidden">
          <div className="px-4 py-3 border-b">
            <p className="text-sm font-medium">{t('subscriptions.advisortab.segmentacion_de_uso_por_area')}</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              {hasClaudeData ? t('subscriptions.advisortab.basado_en_uso_de_claude_anthropic') : t('subscriptions.advisortab.basado_en_todas_las_herramientas_ia')}
            </p>
          </div>
          <table className="w-full text-xs">
            <thead className="bg-muted/40">
              <tr className="text-muted-foreground uppercase tracking-wide">
                <th className="text-left py-2 px-4 font-medium">{t('subscriptions.advisortab.area')}</th>
                <th className="text-right py-2 px-3 font-medium">Gasto</th>
                <th className="text-right py-2 px-3 font-medium">Usuarios</th>
                <th className="text-right py-2 px-3 font-medium">Transacciones</th>
                <th className="text-center py-2 px-3 font-medium">Intensidad</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {areaSegmentation.map(a => (
                <tr key={a.area}>
                  <td className="py-2 px-4 font-medium">{a.area}</td>
                  <td className="py-2 px-3 text-right font-semibold">{fmt(a.amount)}</td>
                  <td className="py-2 px-3 text-right text-muted-foreground">{a.users}</td>
                  <td className="py-2 px-3 text-right text-muted-foreground">{a.transactions}</td>
                  <td className="py-2 px-3 text-center">
                    <IntensityBadge level={a.intensity} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="px-4 py-2 border-t bg-muted/20 flex flex-wrap gap-3">
            {(Object.entries(INTENSITY_CONFIG(t)) as [Intensity, ReturnType<typeof INTENSITY_CONFIG>[Intensity]][]).map(([key, cfg]) => (
              <span key={key} className="text-xs text-muted-foreground">
                <span className="font-medium" style={{ color: cfg.text }}>{cfg.label}:</span> {cfg.desc}
              </span>
            ))}
          </div>
        </Card>
      )}

      {/* 4. Escenarios comparados */}
      <Card className="p-0 overflow-hidden">
        <div className="px-4 py-3 border-b">
          <p className="text-sm font-medium">{t('subscriptions.advisortab.escenarios_de_suscripcion_estimados')}</p>
          <p className="text-xs text-muted-foreground mt-0.5">{t('subscriptions.advisortab.precios_en_usd_seats_usuarios_activos')}</p>
        </div>
        <table className="w-full text-xs">
          <thead className="bg-muted/40">
            <tr className="text-muted-foreground uppercase tracking-wide">
              <th className="text-left py-2 px-4 font-medium">Plan</th>
              <th className="text-right py-2 px-3 font-medium">Seats</th>
              <th className="text-right py-2 px-3 font-medium">Costo/mes</th>
              <th className="text-left py-2 px-3 font-medium">{t('subscriptions.advisortab.ideal_para')}</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {scenarios.map(s => (
              <tr key={s.id} className={s.id === bestScenario.id ? 'bg-[#E5F9F7]/50' : ''}>
                <td className="py-2.5 px-4 font-medium flex items-center gap-2">
                  {s.id === bestScenario.id && <span className="text-brand-green text-[10px]">★</span>}
                  {s.plan}
                </td>
                <td className="py-2.5 px-3 text-right text-muted-foreground">{s.seats}</td>
                <td className="py-2.5 px-3 text-right font-semibold">
                  {s.costMonth < 0 ? 'Custom' : fmt(s.costMonth)}
                </td>
                <td className="py-2.5 px-3 text-muted-foreground">{s.ideal}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="px-4 py-2 text-[10px] text-muted-foreground border-t bg-muted/10">
          {t('subscriptions.advisortab.en_claude_team_los_seats_incluyen')}
        </p>
      </Card>

      {/* 5. Recomendación */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="p-4 border-brand-green border-l-4">
          <p className="text-[10px] font-medium tracking-widest text-brand-green uppercase mb-2">{t('subscriptions.advisortab.recomendacion_principal')}</p>
          <p className="text-base font-semibold text-brand-gray1 dark:text-white">{bestScenario.plan}</p>
          <p className="text-2xl font-bold text-brand-green mt-1">
            {bestScenario.costMonth > 0 ? fmt(bestScenario.costMonth) : '$0'}<span className="text-sm font-normal text-muted-foreground">/mes</span>
          </p>
          <p className="text-xs text-muted-foreground mt-2">{bestScenario.ideal}</p>
        </Card>

        {altScenario && (
          <Card className="p-4 border-brand-gray4 border-l-4">
            <p className="text-[10px] font-medium tracking-widest text-brand-gray3 uppercase mb-2">{t('subscriptions.advisortab.alternativa_economica')}</p>
            <p className="text-base font-semibold text-brand-gray1 dark:text-white">{altScenario.plan}</p>
            <p className="text-2xl font-bold text-brand-gray2 mt-1">
              {altScenario.costMonth > 0 ? fmt(altScenario.costMonth) : '$0'}<span className="text-sm font-normal text-muted-foreground">/mes</span>
            </p>
            <p className="text-xs text-muted-foreground mt-2">{altScenario.ideal}</p>
          </Card>
        )}
      </div>

      {/* 6. Riesgos */}
      <Card className="p-4">
        <p className="text-sm font-medium mb-3">{t('subscriptions.advisortab.puntos_a_validar_antes_de_decidir')}</p>
        <ul className="space-y-1.5 text-xs text-muted-foreground">
          {[
            t('subscriptions.advisortab.risk_1'),
            t('subscriptions.advisortab.risk_2'),
            t('subscriptions.advisortab.risk_3'),
            t('subscriptions.advisortab.risk_4'),
            t('subscriptions.advisortab.risk_5'),
            t('subscriptions.advisortab.risk_6'),
          ].map((risk, i) => (
            <li key={i} className="flex gap-2">
              <span className="text-[#f59e0b] shrink-0">⚠</span>
              {risk}
            </li>
          ))}
        </ul>
      </Card>

      {/* 7. Mensaje sugerido */}
      <Card className="p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-medium">{t('subscriptions.advisortab.mensaje_sugerido_slack_email')}</p>
          <Button variant="outline" size="sm" className="h-7 gap-1.5 text-xs" onClick={copyMessage}>
            {copied ? <Check className="h-3 w-3 text-brand-green" /> : <Copy className="h-3 w-3" />}
            {copied ? 'Copiado' : 'Copiar'}
          </Button>
        </div>
        <pre className="text-xs text-muted-foreground whitespace-pre-wrap bg-muted/30 rounded-md p-3 leading-relaxed font-sans">
          {suggestedMessage}
        </pre>
      </Card>

    </div>
  );
}
